import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { infoUser, saveChanges } from '../api/users';

const updateUserData = (userInfo, defaultValues) => {
  const updatedData = { ...defaultValues };
  
  if (userInfo) {
    Object.keys(defaultValues).forEach(key => {
      if (key === 'info_concourse') {
        updatedData[key] = userInfo[key] !== undefined && userInfo[key] !== null ? userInfo[key] : defaultValues[key];
        return;
      }
      const stateKey = key.replace(/_([a-z])/g, g => g[1].toUpperCase());
      updatedData[stateKey] = userInfo[key] !== undefined && userInfo[key] !== null ? userInfo[key] : defaultValues[key];
      
      if (key === 'benefited_bookstore') {
        updatedData[stateKey] = { 
          value: userInfo[key]?.value || '',
          label: userInfo[key]?.label || '',
          subtitle: userInfo[key]?.subtitle || '',
          url: userInfo[key]?.url || '',
        };      
      }
      if (key === 'isWorker') {
        updatedData[stateKey] = userInfo['type_user'] === 'trabajador';
      }
    });
  }
  
  return updatedData;
};

const defaultValues = {
  id: null,
  username: '',
  first_name: '',
  last_name: '',
  email: '',
  last_password_change: null,
  shipping_address: {},
  billing_address: {},
  pickUpLibrary: null,
  assigned_library: {},
  isWorker: false,
  benefited_bookstore: {},
  date_birth: '',
  dni: '',
  phone_number: '',
  info_concourse: {},
  profile: '',
  is_administrator: false,
  is_concourse_user: false,
  is_special_concourse_user: false
};

const initialState = {
  id: '',
  isLogged: false,
  shippingAddress: {
    address: '',
    postal_code: '',
    first_name: '',
    last_name: '',
    locality: '',
    province: '',
    country: '',
    email: '',
    phone: '',
  },
  billingAddress: {
    address: '',
    postal_code: '',
    first_name: '',
    last_name: '',
    locality: '',
    province: '',
    country: '',
    email: '',
    phone: '',
  },
  pickUpLibrary: null,
  assigned_library: {},
  username: '',
  firstName: '',
  lastName: '',
  email: '',
  loginEmailForRecover: '',
  mustRecoverPassword: null,
  isWorker: false,
  benefitedBookstore: {},
  dateBirth: '',
  dni: '',
  phoneNumber: '',
  status: 'idle',
  error: null,
  info_concourse: {},
  profile: '',
  isAdministrator: false,
  isConcourseUser: false,
  isSpecialConcourseUser: false
};

export const useUserStore = create(
  persist(
    (set, get) => ({
      ...initialState,

      // Acciones síncronas
      loginSuccess: () => set({ isLogged: true }),
      requirePasswordRecover: (email) => set({ mustRecoverPassword: true, loginEmailForRecover: email || '' }),
      completePasswordRecover: () => set({ mustRecoverPassword: false, loginEmailForRecover: '' }),
      
      logoutSuccess: () => set({ 
        isLogged: false, 
        id: null,
        mustRecoverPassword: false,
        loginEmailForRecover: ''
      }),
      
      setId: (id) => set({ id }),
      
      setShippingAddress: (address) => set({ shippingAddress: address }),
      
      setBillingAddress: (address) => set({ billingAddress: address }),
      
      setPickUpLibrary: (library) => set({ pickUpLibrary: library }),
      
      setFirstName: (firstName) => set({ firstName }),
      
      setLastName: (lastName) => set({ lastName }),
      
      setEmail: (email) => set({ email }),
      
      setIsWorker: (isWorker) => set({ isWorker }),
      
      setBenefitedBookstore: (bookstore) => set({ benefitedBookstore: bookstore }),
      
      setAsignedLibrary: (library) => set({ assigned_library: library }),
      
      setDateBirth: (dateBirth) => set({ dateBirth }),
      
      setDni: (dni) => set({ dni }),
      
      setPhoneNumber: (phoneNumber) => set({ phoneNumber }),
      
      loadUser: (savedUser) => {
        if (savedUser && typeof savedUser === 'object') {
          set({
            isLogged: savedUser.isLogged || false,
            id: savedUser.id || '',
            mustRecoverPassword: savedUser.mustRecoverPassword || false,
            username: savedUser.username || '',
            firstName: savedUser.firstName || '',
            lastName: savedUser.lastName || '',
            email: savedUser.email || '',
            isWorker: savedUser.isWorker || false,
            dateBirth: savedUser.dateBirth || '',
            dni: savedUser.dni || '',
            phoneNumber: savedUser.phoneNumber || '',
            shippingAddress: savedUser.shippingAddress || initialState.shippingAddress,
            billingAddress: savedUser.billingAddress || initialState.billingAddress,
            pickUpLibrary: savedUser.pickUpLibrary || null,
            assigned_library: savedUser.assigned_library || {},
            benefitedBookstore: savedUser.benefitedBookstore || {},
            info_concourse: savedUser.info_concourse || {},
            profile: savedUser.profile || '',
            isAdministrator: savedUser.isAdministrator || false,
            isConcourseUser: savedUser.isConcourseUser || false,
            isSpecialConcourseUser: savedUser.isSpecialConcourseUser || false
          });
        }
      },

      // Acciones asíncronas
      fetchUserInfo: async () => {
        set({ status: 'loading' });
        try {
          const response = await infoUser();
          const userData = updateUserData(response.data, defaultValues);
          set({ 
            ...userData,
            status: 'succeeded',
            error: null
          });
          return userData;
        } catch (error) {
          set({ 
            status: 'failed', 
            error: error.message 
          });
          throw error;
        }
      },

      updateUserInfo: async (userData) => {
        set({ status: 'loading' });
        try {
          const response = await saveChanges(userData.data, userData.id);
          const updatedData = updateUserData(response.data, defaultValues);
          const currentState = get();
          set({ 
            ...updatedData,
            mustRecoverPassword: currentState.mustRecoverPassword,
            status: 'succeeded',
            error: null
          });
        } catch (error) {
          set({ 
            status: 'failed', 
            error: error.message 
          });
        }
      }
    }),
    {
      name: 'user-storage',
      partialize: (state) => ({
        isLogged: state.isLogged,
        mustRecoverPassword: state.mustRecoverPassword,
        id: state.id,
        username: state.username,
        firstName: state.firstName,
        lastName: state.lastName,
        email: state.email,
        loginEmailForRecover: state.loginEmailForRecover,
        isWorker: state.isWorker,
        dateBirth: state.dateBirth,
        dni: state.dni,
        phoneNumber: state.phoneNumber,
        shippingAddress: state.shippingAddress,
        billingAddress: state.billingAddress,
        pickUpLibrary: state.pickUpLibrary,
        assigned_library: state.assigned_library,
        benefitedBookstore: state.benefitedBookstore,
        info_concourse: state.info_concourse,
        profile: state.profile,
        isAdministrator: state.isAdministrator,
        isConcourseUser: state.isConcourseUser,
        isSpecialConcourseUser: state.isSpecialConcourseUser
      })
    }
  )
);
